/**
 * Os catálogos fixos das abas da ficha, copiados palavra por palavra do legado: perguntas,
 * itens do Checklist de Milhões, estilos do radar e as etapas da Coloração.
 *
 * UMA CÓPIA SÓ, e é esta: as abas das frentes A2 e A3 (o Dossiê monta as seções com os mesmos
 * rótulos) importam daqui. Duas cópias divergiriam no primeiro ajuste de texto, e o dossiê sairia
 * com uma pergunta escrita diferente da tela.
 *
 * Origem: `portal/admin.html:3097-3288` (perguntas, milhões, estilos e opções da pergunta 23) e
 * `admin.html:5168-5190` (as etapas da Coloração). A ordem das chaves é a do legado, e ela
 * importa: é a ordem em que os campos aparecem na tela e no dossiê.
 */

/** As 13 perguntas do Checklist de Atendimento, coluna por rótulo (`admin.html:3097-3111`). */
export const PERGUNTAS_ATENDIMENTO: Readonly<Record<string, string>> = Object.freeze({
  palavra_chave_imagem: 'Palavra-chave para o desejo de imagem da cliente',
  tecidos_verao_inverno: 'Tecidos que serão a aposta no verão e inverno',
  elementos_essenciais_estilo: 'Elementos importantes que não podem faltar na comunicação de estilo',
  pontos_fortes_silhueta: 'Quais pontos fortes para destacar na silhueta',
  pontos_equilibrio_silhueta: 'Quais pontos para dar equilíbrio na silhueta',
  cortes_e_caimentos: 'Sugerir formas de cortes e caimentos',
  cores_no_vestir: 'Quais cores aplicar no vestir',
  make_dia_e_noite: 'O que priorizar na make do dia e da noite',
  acessorios_prioritarios: 'Quais acessórios priorizar',
  combinacoes_circulo_cromatico: 'Combinações que serão priorizadas a partir do círculo cromático',
  estampas_e_contraste: 'Quais estampas e contraste de estampas usar',
  tons_de_cabelo: 'Quais tons de cabelo priorizar para o desejo de imagem atual',
  sapatos_e_bolsas: 'Quais modelos de sapatos e bolsas priorizar',
});

/** As 15 perguntas do Questionário Avaliativo; a última é o depoimento (`admin.html:3113-3129`). */
export const PERGUNTAS_AVALIATIVO: Readonly<Record<string, string>> = Object.freeze({
  micropigmentacao:
    'Já fez micropigmentação? (Sobrancelhas, boca ou outro procedimento que insira pigmento no rosto).',
  coloracao_cabelos: 'Tem coloração nos cabelos? Se sim, costuma colorir de quanto em quanto tempo?',
  motivacao_teste_coloracao: 'O que te motivou a fazer o teste coloração pessoal?',
  prolongamento_cilios: 'Possuí prolongamento de cílios?',
  cores_dia_a_dia: 'Usa cores no dia-a-dia? (Maquiagem, roupas, acessórios).',
  dificuldade_tom_produtos: 'Tem dificuldade em encontrar produtos no tom certo da sua pele?',
  batons_marcantes: 'Gosta de usar batons mais marcantes? Como vermelho.',
  frequencia_maquiagem: 'Usa maquiagem com frequência? Quais são seus produtos indispensáveis para o dia-a-dia?',
  marca_maquiagem_preferida: 'Alguma marca de maquiagem que gosta ou costuma usar?',
  adjetivos_imagem_pessoal: 'Em adjetivos, o que gostaria de comunicar em sua imagem pessoal?',
  cor_preferida: 'Tem uma cor preferida? Qual?',
  cor_que_nao_gosta: 'Alguma cor que não gosta e não usa?',
  cor_predominante_estilo: 'Qual a cor predominante em seu estilo pessoal e guarda-roupas?',
  como_soube_trabalho: 'Como soube do meu trabalho?',
  depoimento_texto: 'Como você descreveria sua experiência com a consultoria da Lu até agora?',
});

/** Um item do Checklist de Milhões: a coluna booleana no banco e os dois textos da tela. */
export interface ItemMilhoes {
  readonly col: string;
  readonly elemento: string;
  readonly efeito: string;
}

/** Os 77 itens do Checklist de Milhões, na ordem do legado (`admin.html:3131-3210`). */
export const ITENS_MILHOES: readonly ItemMilhoes[] = Object.freeze([
  { col: 'decote_u', elemento: 'Decote U', efeito: 'Mantém a proporção do busto e ameniza volume' },
  { col: 'decote_v', elemento: 'Decote V', efeito: 'Ameniza o volume do busto e alonga a silhueta e pescoço' },
  { col: 'decote_canoa', elemento: 'Decote canoa', efeito: 'Alonga os ombros' },
  { col: 'decote_quadrado', elemento: 'Decote quadrado', efeito: 'Valoriza o colo' },
  { col: 'decote_fechado', elemento: 'Decote fechado', efeito: 'Aumenta e destaca volume no busto' },
  { col: 'decote_profundo', elemento: 'Decote profundo', efeito: 'Alonga consideravelmente a silhueta' },
  { col: 'decote_assimetrico', elemento: 'Decote assimétrico', efeito: 'Ameniza ombros expressivos' },
  { col: 'gola_alta', elemento: 'Gola alta', efeito: 'Alonga e valoriza o colo' },
  { col: 'gola_alta_volumosa', elemento: 'Gola alta volumosa', efeito: 'Traz volume para o pescoço' },
  { col: 'alca_larga', elemento: 'Alça larga', efeito: 'Traz volume para os ombros' },
  { col: 'alca_media', elemento: 'Alça média', efeito: 'Mantém o formato natural dos ombros' },
  { col: 'alca_fina', elemento: 'Alça fina', efeito: 'Ameniza a região do colo' },
  { col: 'mangas_3_4', elemento: 'Mangas 3/4', efeito: 'Alonga a silhueta' },
  { col: 'mangas_longas', elemento: 'Mangas longas', efeito: 'Se volumosas no punho, evidenciam o quadril' },
  {
    col: 'mangas_curtas',
    elemento: 'Mangas curtas: musculosa/tee, bufante, princesa',
    efeito: 'Trazem volume e imponência para os ombros',
  },
  {
    col: 'manga_flare_ombro_cotovelo',
    elemento: 'Manga flare entre ombro e cotovelo',
    efeito: 'Ameniza o volume dos braços',
  },
  { col: 'manga_justa', elemento: 'Manga justa', efeito: 'Destaca o volume dos braços' },
  {
    col: 'blusa_contrastante_alca_saia',
    elemento: 'Blusa contrastante com a alça/saia',
    efeito: 'Chama atenção para a região do corte',
  },
  {
    col: 'blusa_nao_contrastante_calca_saia',
    elemento: 'Blusa não contrastante com calça/saia',
    efeito: 'Alonga a silhueta',
  },
  { col: 'terceira_peca_cropped', elemento: 'Terceira peça cropped', efeito: 'Evidencia o busto' },
  {
    col: 'terceira_peca_acima_quadril',
    elemento: 'Terceira peça acima do quadril',
    efeito: 'Evidencia e traz volume para o quadril',
  },
  {
    col: 'terceira_peca_linha_quadril',
    elemento: 'Terceira peça na linha do quadril',
    efeito: 'Evidencia o quadril de forma mais sutil',
  },
  {
    col: 'terceira_peca_abaixo_quadril',
    elemento: 'Terceira peça abaixo do quadril',
    efeito: 'Ameniza o quadril e pode alongar a silhueta, se o tom for próximo da parte de baixo',
  },
  {
    col: 'terceira_peca_midi',
    elemento: 'Terceira peça midi',
    efeito: 'Pode encurtar, se for contrastante com calça e sapato',
  },
  { col: 'terceira_peca_longa', elemento: 'Terceira peça longa', efeito: 'Alonga a silhueta' },
  {
    col: 'blazer_lapela_fina',
    elemento: 'Blazer ou jaqueta com lapela fina',
    efeito: 'Alonga e mantém o volume natural da região',
  },
  {
    col: 'blazer_lapela_grossa',
    elemento: 'Blazer ou jaqueta lapela grossa',
    efeito: 'Destaca a região e pode dar volume',
  },
  {
    col: 'calca_acima_tornozelo',
    elemento: 'Calça acima do tornozelo',
    efeito: 'Pode encurtar a silhueta se contrastante com a cor do sapato',
  },
  {
    col: 'calca_comprimento_longo',
    elemento: 'Calça com comprimento longo',
    efeito: 'Alonga a silhueta quando encobre os pés, ou mostra o sapato mas em tons próximos',
  },
  { col: 'calca_com_nuance_cor', elemento: 'Calça com nuance de cor', efeito: 'Traz mais volume para as pernas' },
  { col: 'calca_sem_nuance_cor', elemento: 'Calça sem nuance de cor', efeito: 'Mantém o tamanho real da região' },
  {
    col: 'calca_amarracao_lateral',
    elemento: 'Calça com amarração lateral',
    efeito: 'Ameniza a barriga saliente se o laço não for muito volumoso',
  },
  { col: 'calca_com_pregas', elemento: 'Calça com pregas', efeito: 'Traz volume para o quadril' },
  { col: 'calca_sem_pregas', elemento: 'Calça sem pregas', efeito: 'Mantém o volume da região' },
  { col: 'calca_reta', elemento: 'Calça reta', efeito: 'Modelo democrático' },
  { col: 'calca_skinny', elemento: 'Calça skinny', efeito: 'Evidencia o quadril e volume das pernas' },
  { col: 'calca_mom', elemento: 'Calça mom', efeito: 'Valoriza o quadril' },
  {
    col: 'calca_clochard',
    elemento: 'Calça clochard',
    efeito: 'Traz volume para o quadril, mas pode evidenciar abdômen',
  },
  { col: 'calca_slouchy', elemento: 'Calça slouchy', efeito: 'Traz volume para pernas e quadril' },
  { col: 'calca_wide_leg', elemento: 'Calça wide leg', efeito: 'Traz volume para o quadril' },
  { col: 'calca_pantalona', elemento: 'Calça pantalona', efeito: 'Alonga a silhueta' },
  {
    col: 'calca_cargo',
    elemento: 'Calça cargo',
    efeito: 'Traz volume para a parte inferior pelo volume de detalhes que possui',
  },
  {
    col: 'calca_pantacourt',
    elemento: 'Calça pantacourt',
    efeito: 'Encurta a silhueta, pode trazer volume para o quadril',
  },
  { col: 'saia_mini_secretaria', elemento: 'Saia comprimento mini e secretária', efeito: 'Alonga as pernas' },
  { col: 'saia_chanel', elemento: 'Saia comprimento Chanel', efeito: 'Encurta a silhueta (observe a cor)' },
  { col: 'saia_longuete', elemento: 'Saia comprimento longuete', efeito: 'Encurta a silhueta (observe a cor)' },
  {
    col: 'saia_longo',
    elemento: 'Saia comprimento longo',
    efeito: 'Pode alongar a silhueta quando combinada com salto alto não aparente',
  },
  { col: 'saia_evase', elemento: 'Saia evasê', efeito: 'Evidencia a cintura, ameniza pernas grossas' },
  { col: 'saia_gode', elemento: 'Saia godê', efeito: 'Traz volume para o quadril' },
  { col: 'saia_reta', elemento: 'Saia reta', efeito: 'Ameniza quadril e pernas grossas' },
  { col: 'saia_justa_lapis', elemento: 'Saia justa ou lápis', efeito: 'Evidencia o quadril e pernas' },
  {
    col: 'saia_plissada',
    elemento: 'Saia plissada',
    efeito: 'Dependendo do tecido, pode dar volume para o quadril',
  },
  { col: 'saia_babados', elemento: 'Saia com babados', efeito: 'Traz volume para a região' },
  { col: 'saia_drapeado_lateral', elemento: 'Saia com drapeado lateral', efeito: 'Ameniza o quadril' },
  { col: 'saia_fenda', elemento: 'Saia com fenda', efeito: 'Alonga a silhueta' },
  {
    col: 'brincos_alongados',
    elemento: 'Brincos alongados',
    efeito:
      'Evidenciam a região do pescoço; se o pescoço for fino, esse modelo pode evidenciar essa característica',
  },
  {
    col: 'brincos_pontuais',
    elemento: 'Brincos pontuais',
    efeito: 'Trazem volume para o maxilar quando expressivos',
  },
  {
    col: 'pulseira_expressiva_bracelete',
    elemento: 'Pulseira expressiva ou bracelete',
    efeito: 'Podem chamar atenção para o quadril',
  },
  { col: 'pulseira_delicada', elemento: 'Pulseira delicada', efeito: 'Não influencia na proporção' },
  { col: 'aneis_expressivos', elemento: 'Anéis expressivos', efeito: 'Chamam atenção' },
  { col: 'aneis_delicados', elemento: 'Anéis delicados', efeito: 'Não influenciam na proporção' },
  {
    col: 'sapatos_coloridos',
    elemento: 'Sapatos coloridos',
    efeito: 'Podem encurtar as pernas se contrastarem com calça, saia ou perna',
  },
  { col: 'sapatos_nudes', elemento: 'Sapatos nudes', efeito: 'Alongam a silhueta' },
  { col: 'sapatos_bico_fino', elemento: 'Sapatos bico fino', efeito: 'Alongam a silhueta' },
  {
    col: 'sapatos_bico_arredondado',
    elemento: 'Sapatos bico arredondado',
    efeito: 'Mantém a proporção natural; sapatilhas podem encurtar a silhueta',
  },
  { col: 'sapatos_bico_quadrado', elemento: 'Sapatos bico quadrado', efeito: 'Podem encurtar a silhueta' },
  {
    col: 'sapatos_tiras_largas',
    elemento: 'Sapatos tiras largas e expressivas',
    efeito: 'Encurtam as pernas se amarradas no tornozelo',
  },
  {
    col: 'sapatos_tiras_finas',
    elemento: 'Sapatos tiras finas e delicadas',
    efeito: 'Podem encurtar a silhueta dependendo da cor',
  },
  { col: 'sapatos_sem_tiras', elemento: 'Sapatos sem tiras', efeito: 'Alongam a silhueta' },
  { col: 'bico_pes_amostra', elemento: 'Bico dos pés à mostra', efeito: 'Alonga a silhueta' },
  { col: 'bolsa_acima_quadril', elemento: 'Bolsa acima do quadril', efeito: 'Pode evidenciar o abdômen' },
  {
    col: 'bolsa_altura_quadril',
    elemento: 'Bolsa na altura do quadril',
    efeito: 'Mantém o volume do quadril quando a cor não é contrastante',
  },
  {
    col: 'bolsa_apoiada_ombro',
    elemento: 'Bolsa apoiada no ombro',
    efeito: 'Se estiver na altura do seio, chama atenção pra essa região',
  },
  { col: 'bolsa_apoiada_mao', elemento: 'Bolsa apoiada na mão', efeito: 'Traz volume para o quadril' },
  {
    col: 'bolsa_grande',
    elemento: 'Bolsa grande',
    efeito: 'Para proporções corporais com mais estrutura, harmoniza mais',
  },
  { col: 'bolsa_media', elemento: 'Bolsa média', efeito: 'Democrática' },
  {
    col: 'bolsa_pequena',
    elemento: 'Bolsa pequena',
    efeito: 'Observe o modelo para não destoar muito da proporção da região onde ela fica em relação ao tamanho',
  },
]);

/**
 * As 37 colunas do Questionário Geral com o rótulo CURTO do legado (`admin.html:3212-3250`).
 *
 * Estes rótulos NÃO são os do formulário (que são longos, "7. Sua profissão e como é seu dia a
 * dia..."): são os do dossiê e das respostas lidas. O formulário da aba tem os próprios rótulos,
 * escritos na aba, como no HTML do legado. As CHAVES servem às duas: é a lista das colunas que o
 * salvar percorre (`Object.keys(PERGUNTAS_GERAL)`, `admin.html:4862`).
 */
export const PERGUNTAS_GERAL: Readonly<Record<string, string>> = Object.freeze({
  nome_completo: '1. Nome completo',
  endereco: '2. Endereço',
  telefone: '3. Telefone',
  email: '4. E-mail',
  estado_civil: '5. Estado civil',
  tem_filhos: '6. Tem filhos?',
  profissao_rotina: '7. Profissão e rotina (mais fora ou escritório)',
  traje_recomendado_trabalho: '8. Existe traje recomendado no trabalho?',
  rotina_fins_de_semana: '9. Rotina nos fins de semana',
  vida_noturna: '10. Vida noturna',
  uso_maquiagem: '11. Uso de maquiagem',
  rotina_diaria: '12. Rotina diária pra se arrumar',
  cores_preferidas: '13a. Cores que mais usa e gosta',
  cores_evitadas: '13b. Cores que evita',
  gosta_estampas: '14a. Gosta de estampas? De que tipo?',
  tecidos_preferidos: '14b. Tecidos que prefere',
  tecidos_evitados: '14c. Tecidos que evita',
  acessorios_preferencia: '15. Acessórios e tamanho preferido',
  peca_favorita: '16a. Peça favorita do guarda-roupa',
  peca_que_evita: '16b. Peça ou formato que evita',
  estilo_admirado: '17. Pessoa cujo estilo admira',
  locais_compra: '18. Onde costuma comprar',
  percepcao_preco: '19. O que considera caro, barato ou justo',
  marcas_preferidas: '20. Marcas preferidas',
  parte_corpo_valorizada: '21. Parte do corpo que mais valoriza',
  dores_imagem_atual: '22. Dores com a imagem hoje',
  imagem_desejada: '23a. Imagem que quer passar',
  sensacao_desejada: '23b. Como quer se sentir',
  objetivos_momento: '23c. O que motiva a consultoria agora',
  obstaculos_vestir: '24. O que impede de se vestir como gostaria',
  dificuldades_atuais: '25. O que mais pesa no guarda-roupa hoje',
  expectativa_consultoria: '26. Expectativa com a consultoria',
  orcamento_compras: '27. Orçamento pra peças novas',
  tamanho_cima: '28a. Tamanho: cima',
  tamanho_baixo: '28b. Tamanho: baixo',
  tamanho_lingerie: '28c. Tamanho: lingerie',
  tamanho_sapato: '28d. Tamanho: sapato',
});

/** As 7 colunas da Personalidade com o rótulo de cada uma (`admin.html:3252-3260`). */
export const PERGUNTAS_PERSONALIDADE: Readonly<Record<string, string>> = Object.freeze({
  coluna_1: 'Coluna 1',
  coluna_2: 'Coluna 2',
  coluna_3: 'Coluna 3',
  coluna_4: 'Coluna 4',
  coluna_5: 'Coluna 5',
  coluna_6: 'Coluna 6',
  coluna_7: 'Coluna 7',
});

/**
 * Os sete estilos do radar, na ordem fixa das colunas da Personalidade: posição 1 é Casual e
 * posição 7 é Moderno (`admin.html:3262-3271`).
 */
export const ESTILOS_RAIZ: readonly { readonly estilo: string; readonly coluna: string }[] = Object.freeze([
  { estilo: 'CASUAL', coluna: 'coluna_1' },
  { estilo: 'CLASSICO', coluna: 'coluna_2' },
  { estilo: 'ELEGANTE', coluna: 'coluna_3' },
  { estilo: 'ROMANTICO', coluna: 'coluna_4' },
  { estilo: 'SENSUAL', coluna: 'coluna_5' },
  { estilo: 'CRIATIVO', coluna: 'coluna_6' },
  { estilo: 'MODERNO', coluna: 'coluna_7' },
]);

/**
 * As sete opções de cada pergunta 23, na ordem do formulário: a posição da opção marcada soma um
 * ponto ao estilo da mesma posição no radar (`admin.html:3273-3288`).
 */
export const OPCOES_Q23: Readonly<Record<string, readonly string[]>> = Object.freeze({
  imagem_desejada: [
    'Acolhedora e acessível',
    'Confiável e ponderada',
    'Culta e serena',
    'Carinhosa e empática',
    'Marcante e vibrante',
    'Criativa e original',
    'Segura e cosmopolita',
  ],
  sensacao_desejada: [
    'Mais jovem e energizada',
    'Com credibilidade e presença',
    'Refinada e elegante',
    'Generosa e acolhedora',
    'Confiante e atraente',
    'Livre e independente',
    'Forte e determinada',
  ],
  objetivos_momento: [
    'Reduzir o estresse do dia a dia e me divertir mais',
    'Ser vista como mais organizada e responsável',
    'Ganhar mais status e prestígio',
    'Construir relações mais profundas',
    'Atrair e engajar mais pessoas',
    'Expressar minha individualidade',
    'Aumentar minha influência',
  ],
});

/** Uma etapa de marcar da Coloração: a coluna, o título exato e as opções exatas. */
export interface EtapaDeColoracao {
  readonly campo: string;
  readonly titulo: string;
  readonly opcoes: readonly string[];
}

/** As etapas 1 a 5 da Coloração (`ETAPAS_COLORACAO_CHECKBOX`, `admin.html:5168-5174`). */
export const ETAPAS_COLORACAO_CHECKBOX: readonly EtapaDeColoracao[] = Object.freeze([
  {
    campo: 'contraste_pessoal',
    titulo: 'Etapa 1 | Contraste Pessoal',
    opcoes: ['Alto', 'Médio/Alto', 'Médio', 'Médio/Baixo Claro', 'Médio/Baixo Escuro', 'Baixo Claro', 'Baixo Escuro'],
  },
  {
    campo: 'subtom_pele',
    titulo: 'Etapa 2 | Subtom de Pele',
    opcoes: ['Quente', 'Subtom Neutro Quente', 'Subtom Frio', 'Subtom Neutro Frio'],
  },
  { campo: 'metais', titulo: 'Etapa 3 | Metais', opcoes: ['Dourado', 'Rosé', 'Prata'] },
  { campo: 'preto_branco', titulo: 'Etapa 4 | Preto e Branco', opcoes: ['Preto', 'Preto Fosco', 'Branco', 'Off White'] },
  {
    campo: 'dimensoes',
    titulo: 'Etapa 5 | Dimensões',
    opcoes: ['Intenso', 'Quente', 'Frio', 'Claro', 'Escuro', 'Suave'],
  },
]);

/** As cinco famílias de cor da etapa 8 (`FAMILIAS_CORES_COLORACAO`, `admin.html:5176-5182`). */
export const FAMILIAS_CORES_COLORACAO: readonly { readonly campo: string; readonly titulo: string }[] = Object.freeze([
  { campo: 'cor_amarelos', titulo: 'Amarelos' },
  { campo: 'cor_azuis', titulo: 'Azuis' },
  { campo: 'cor_rosas', titulo: 'Rosas' },
  { campo: 'cor_verdes', titulo: 'Verdes' },
  { campo: 'cor_vermelhos', titulo: 'Vermelhos' },
]);

/** As quatro estações que cada família de cor pode marcar (`OPCOES_ESTACAO_SIMPLES`, `admin.html:5183`). */
export const OPCOES_ESTACAO_SIMPLES: readonly string[] = Object.freeze(['Primavera', 'Outono', 'Inverno', 'Verão']);

/** As 12 estações da etapa 9, na ordem da lista (`ESTACOES_12`, `admin.html:5185-5190`). */
export const ESTACOES_12: readonly string[] = Object.freeze([
  'Primavera Quente',
  'Primavera Clara',
  'Primavera Brilhante',
  'Outono Quente',
  'Outono Escuro',
  'Outono Suave',
  'Inverno Frio',
  'Inverno Escuro',
  'Inverno Brilhante',
  'Verão Frio',
  'Verão Claro',
  'Verão Suave',
]);
